// DeliveryRouteDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Grid, Typography, Paper, Card, CardContent, TextField,
    CircularProgress, Button, IconButton, Tooltip, Chip, Divider,
    useMediaQuery, useTheme, Fade, Zoom, Grow, LinearProgress,
    Avatar, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TableSortLabel, MenuItem, Select, FormControl, InputLabel,
    Alert, Collapse, Switch, FormControlLabel, Tabs, Tab, Badge,
    Stack, Stepper, Step, StepLabel, StepContent, Accordion, AccordionSummary, AccordionDetails,
    Drawer, List, ListItem, ListItemIcon, ListItemText, SpeedDial, SpeedDialAction, SpeedDialIcon,
    Dialog, DialogTitle, DialogContent, DialogActions, Icon
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../../../lib/axios';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';

// PDF Export
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import TodayIcon from '@mui/icons-material/Today';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RouteIcon from '@mui/icons-material/Route';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoIcon from '@mui/icons-material/Info';

// Charts
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const DeliveryRouteDashboard = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const globalProperty = useSelector(selectGlobalProperty);

    // State variables
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState('ALL');
    const [startDate, setStartDate] = useState(dayjs('2026-05-01'));
    const [endDate, setEndDate] = useState(dayjs());
    const [searchText, setSearchText] = useState('');
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [error, setError] = useState(null);
    const [isPropertyReady, setIsPropertyReady] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [routeDetailOpen, setRouteDetailOpen] = useState(false);
    const [selectedRouteDetail, setSelectedRouteDetail] = useState(null);
    const [instituteDetailOpen, setInstituteDetailOpen] = useState(false);
    const [selectedInstituteDetail, setSelectedInstituteDetail] = useState(null);
    const [deliveryStats, setDeliveryStats] = useState({
        totalOrders: 0,
        totalQuantity: 0,
        deliveredQuantity: 0,
        returnedQuantity: 0,
        pendingQuantity: 0,
        cancelledQuantity: 0,
        completionRate: 0,
        returnRate: 0,
        onTimeRate: 0
    });
    const [routeStats, setRouteStats] = useState([]);
    const [instituteStats, setInstituteStats] = useState([]);
    const [dailyTrends, setDailyTrends] = useState([]);
    const [statusDistribution, setStatusDistribution] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(500);
    const [totalRecords, setTotalRecords] = useState(0);
    const [animateStats, setAnimateStats] = useState(false);
    const [exporting, setExporting] = useState(false);

    // COLORS for charts
    const COLORS = {
        primary: '#635bff',
        success: '#22c55e',
        warning: '#f59e0b',
        error: '#ef4444',
        info: '#3b82f6',
        purple: '#8b5cf6',
        pink: '#ec4899',
        teal: '#14b8a6',
        orange: '#f97316',
        indigo: '#6366f1'
    };

    const CHART_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.error, COLORS.info, COLORS.purple, COLORS.pink, COLORS.teal];

    // Initialize property
    useEffect(() => {
        const initializeProperty = () => {
            const savedPropertyId = localStorage.getItem('selectedPropertyId');
            const isLocked = localStorage.getItem('propertyLocked') === 'true';
            if (savedPropertyId && isLocked && !globalProperty) {
                // dispatch(setGlobalProperty(parseInt(savedPropertyId)));
            }
            setIsPropertyReady(true);
        };
        initializeProperty();
    }, [dispatch, globalProperty]);

    // Fetch orders from API
    const fetchOrders = async () => {
        if (!isPropertyReady) return;
        
        if (!globalProperty) {
            toast.warning('Please select a property first');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const UserId = sessionStorage?.getItem('UserId');
            if (!UserId) {
                toast.error('User not found. Please login again.');
                return;
            }

            const payload = {
                PageNumber: pageNumber,
                PageSize: pageSize,
                Id: 0,
                SearchText: searchText,
                SearchFrom: startDate.format('YYYY-MM-DDTHH:mm:ss'),
                SearchTo: endDate.format('YYYY-MM-DDTHH:mm:ss'),
                Flag: "ALL"
            };

            console.log('Fetching delivery data with payload:', payload);

            const response = await axiosInstance.post('TrnTiffin/GetTrnTiffinDashBoard', payload);

            if (response.data && (response.data.Status === 0 || response.data.Status === 200)) {
                if (response.data.Data && Array.isArray(response.data.Data) && response.data.Data.length > 0) {
                    setOrders(response.data.Data);
                    setFilteredOrders(response.data.Data);
                    setTotalRecords(response.data.TotalRowCount || response.data.Data.length);
                    processRouteData(response.data.Data);
                    calculateStats(response.data.Data);
                    processDailyTrends(response.data.Data);
                    processInstituteStats(response.data.Data);
                    processStatusDistribution(response.data.Data);
                    toast.success(`Loaded ${response.data.Data.length} orders successfully!`);
                } else {
                    setOrders([]);
                    setFilteredOrders([]);
                    setRoutes([]);
                    resetStats();
                    toast.info('No orders found for the selected criteria');
                }
            } else {
                throw new Error(response.data?.Message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching delivery data:', error);
            let errorMessage = 'Failed to load delivery data. ';
            if (error.response?.data?.Message) errorMessage += error.response.data.Message;
            else if (error.message) errorMessage += error.message;
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setAnimateStats(true);
        }
    };

    // Reset stats
    const resetStats = () => {
        setDeliveryStats({
            totalOrders: 0, totalQuantity: 0, deliveredQuantity: 0,
            returnedQuantity: 0, pendingQuantity: 0, cancelledQuantity: 0,
            completionRate: 0, returnRate: 0, onTimeRate: 0
        });
        setRouteStats([]);
        setInstituteStats([]);
        setDailyTrends([]);
        setStatusDistribution([]);
    };

    // Process route data
    const processRouteData = (data) => {
        const routeMap = new Map();
        
        data.forEach(order => {
            const routeNo = order.RouteNo || 'UNASSIGNED';
            if (!routeMap.has(routeNo)) {
                routeMap.set(routeNo, {
                    routeNo: routeNo,
                    totalOrders: 0,
                    totalQuantity: 0,
                    deliveredQuantity: 0,
                    returnedQuantity: 0,
                    pendingQuantity: 0,
                    cancelledQuantity: 0,
                    institutes: new Set(),
                    locations: new Set(),
                    orders: []
                });
            }
            
            const route = routeMap.get(routeNo);
            route.totalOrders++;
            route.totalQuantity += (order.Req_Qty || 0);
            route.deliveredQuantity += (order.Dlv_Qty || 0);
            route.returnedQuantity += (order.Rtn_Qty || 0);
            if (order.Tfn_ReqSt?.toLowerCase() === 'pending') route.pendingQuantity += (order.Req_Qty || 0);
            if (order.Tfn_ReqSt?.toLowerCase() === 'cancelled') route.cancelledQuantity += (order.Req_Qty || 0);
            route.institutes.add(order.InstituteName);
            if (order.Dlv_Loc) route.locations.add(order.Dlv_Loc);
            route.orders.push(order);
        });

        const routeArray = Array.from(routeMap.values()).map(route => ({
            ...route,
            institutesList: Array.from(route.institutes),
            locationsList: Array.from(route.locations),
            institutes: route.institutes.size,
            locations: route.locations.size,
            completionRate: route.totalQuantity > 0 ? (route.deliveredQuantity / route.totalQuantity) * 100 : 0,
            returnRate: route.deliveredQuantity > 0 ? (route.returnedQuantity / route.deliveredQuantity) * 100 : 0
        }));

        setRoutes(routeArray);
        setRouteStats(routeArray);
    };

    // Calculate overall statistics
    const calculateStats = (data) => {
        const totalOrders = data.length;
        const totalQuantity = data.reduce((sum, o) => sum + (o.Req_Qty || 0), 0);
        const deliveredQuantity = data.reduce((sum, o) => sum + (o.Dlv_Qty || 0), 0);
        const returnedQuantity = data.reduce((sum, o) => sum + (o.Rtn_Qty || 0), 0);
        const pendingQuantity = data.filter(o => o.Tfn_ReqSt?.toLowerCase() === 'pending').reduce((sum, o) => sum + (o.Req_Qty || 0), 0);
        const cancelledQuantity = data.filter(o => o.Tfn_ReqSt?.toLowerCase() === 'cancelled').reduce((sum, o) => sum + (o.Req_Qty || 0), 0);
        
        const onTimeDeliveries = data.filter(o => {
            if (!o.Dlv_Act_Time || !o.Dlv_Time) return false;
            const actualTime = dayjs(o.Dlv_Act_Time);
            const expectedTime = dayjs(o.Dlv_Time);
            return actualTime.isBefore(expectedTime) || actualTime.isSame(expectedTime);
        }).length;

        setDeliveryStats({
            totalOrders,
            totalQuantity,
            deliveredQuantity,
            returnedQuantity,
            pendingQuantity,
            cancelledQuantity,
            completionRate: totalQuantity > 0 ? (deliveredQuantity / totalQuantity) * 100 : 0,
            returnRate: deliveredQuantity > 0 ? (returnedQuantity / deliveredQuantity) * 100 : 0,
            onTimeRate: deliveredQuantity > 0 ? (onTimeDeliveries / data.filter(o => o.Dlv_Qty > 0).length) * 100 : 0
        });
    };

    // Process daily trends
    const processDailyTrends = (data) => {
        const dailyMap = new Map();
        
        data.forEach(order => {
            const date = dayjs(order.Req_Date).format('YYYY-MM-DD');
            if (!dailyMap.has(date)) {
                dailyMap.set(date, { date, delivered: 0, returned: 0, pending: 0, total: 0 });
            }
            const record = dailyMap.get(date);
            record.total += (order.Req_Qty || 0);
            record.delivered += (order.Dlv_Qty || 0);
            record.returned += (order.Rtn_Qty || 0);
            if (order.Tfn_ReqSt?.toLowerCase() === 'pending') record.pending += (order.Req_Qty || 0);
        });

        setDailyTrends(Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date)));
    };

    // Process institute statistics
    const processInstituteStats = (data) => {
        const instituteMap = new Map();
        
        data.forEach(order => {
            const institute = order.InstituteName || 'Unknown';
            if (!instituteMap.has(institute)) {
                instituteMap.set(institute, {
                    name: institute,
                    totalOrders: 0,
                    totalQuantity: 0,
                    deliveredQuantity: 0,
                    returnedQuantity: 0,
                    routes: new Set(),
                    orders: []
                });
            }
            const inst = instituteMap.get(institute);
            inst.totalOrders++;
            inst.totalQuantity += (order.Req_Qty || 0);
            inst.deliveredQuantity += (order.Dlv_Qty || 0);
            inst.returnedQuantity += (order.Rtn_Qty || 0);
            if (order.RouteNo) inst.routes.add(order.RouteNo);
            inst.orders.push(order);
        });

        setInstituteStats(Array.from(instituteMap.values()).map(inst => ({
            ...inst,
            routesList: Array.from(inst.routes),
            routes: inst.routes.size,
            completionRate: inst.totalQuantity > 0 ? (inst.deliveredQuantity / inst.totalQuantity) * 100 : 0
        })).sort((a, b) => b.totalQuantity - a.totalQuantity).slice(0, 10));
    };

    // Process status distribution
    const processStatusDistribution = (data) => {
        const statusMap = new Map();
        
        data.forEach(order => {
            const status = order.Tfn_ReqSt || 'Unknown';
            if (!statusMap.has(status)) {
                statusMap.set(status, { name: status, value: 0, count: 0 });
            }
            const record = statusMap.get(status);
            record.value += (order.Req_Qty || 0);
            record.count++;
        });

        setStatusDistribution(Array.from(statusMap.values()));
    };

    // Apply filters
    const applyFilters = useCallback(() => {
        let filtered = [...orders];
        
        if (selectedRoute !== 'ALL') {
            filtered = filtered.filter(order => order.RouteNo === selectedRoute);
        }
        
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(order => 
                order.InstituteName?.toLowerCase().includes(searchLower) ||
                order.ResidentName?.toLowerCase().includes(searchLower) ||
                order.MobileNo?.includes(searchText) ||
                order.Tfn_ReqId?.toString().includes(searchText)
            );
        }
        
        filtered = filtered.filter(order => {
            if (!order.Req_Date) return false;
            const orderDate = dayjs(order.Req_Date);
            return orderDate.isAfter(startDate.subtract(1, 'day')) && orderDate.isBefore(endDate.add(1, 'day'));
        });
        
        setFilteredOrders(filtered);
        
        if (selectedRoute !== 'ALL') {
            const routeData = routeStats.find(r => r.routeNo === selectedRoute);
            if (routeData) {
                setDeliveryStats({
                    totalOrders: routeData.totalOrders,
                    totalQuantity: routeData.totalQuantity,
                    deliveredQuantity: routeData.deliveredQuantity,
                    returnedQuantity: routeData.returnedQuantity,
                    pendingQuantity: routeData.pendingQuantity,
                    cancelledQuantity: routeData.cancelledQuantity,
                    completionRate: routeData.completionRate,
                    returnRate: routeData.returnRate,
                    onTimeRate: deliveryStats.onTimeRate
                });
            }
        } else {
            calculateStats(filtered);
        }
        
        processDailyTrends(filtered);
        processInstituteStats(filtered);
        processStatusDistribution(filtered);
    }, [orders, selectedRoute, searchText, startDate, endDate, routeStats]);

    // Reset all filters
    const resetFilters = () => {
        setSelectedRoute('ALL');
        setSearchText('');
        setStartDate(dayjs().subtract(7, 'day'));
        setEndDate(dayjs());
        setFilteredOrders(orders);
        if (orders.length > 0) {
            calculateStats(orders);
            processDailyTrends(orders);
            processInstituteStats(orders);
            processStatusDistribution(orders);
        }
    };

    // Handle route card click - show route details modal
    const handleRouteClick = (route) => {
        setSelectedRouteDetail(route);
        setRouteDetailOpen(true);
    };

    // Handle institute card click - show institute details modal
    const handleInstituteClick = (institute) => {
        setSelectedInstituteDetail(institute);
        setInstituteDetailOpen(true);
    };

    // Get sorted orders
    const getSortedOrders = () => {
        return [...filteredOrders].sort((a, b) => {
            if (sortBy === 'date') {
                return sortOrder === 'desc'
                    ? new Date(b.Req_Date) - new Date(a.Req_Date)
                    : new Date(a.Req_Date) - new Date(b.Req_Date);
            }
            if (sortBy === 'quantity') {
                return sortOrder === 'desc'
                    ? (b.Req_Qty || 0) - (a.Req_Qty || 0)
                    : (a.Req_Qty || 0) - (b.Req_Qty || 0);
            }
            if (sortBy === 'institute') {
                return sortOrder === 'desc'
                    ? (b.InstituteName || '').localeCompare(a.InstituteName || '')
                    : (a.InstituteName || '').localeCompare(b.InstituteName || '');
            }
            return 0;
        });
    };

    // Get status chip
    const getStatusChip = (status, quantity = null) => {
        const statusConfig = {
            'Delivered': { bg: '#22c55e15', color: '#22c55e', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
            'Returned': { bg: '#f59e0b15', color: '#f59e0b', icon: <CancelIcon sx={{ fontSize: 14 }} /> },
            'Booked': { bg: '#3b82f615', color: '#3b82f6', icon: <PendingIcon sx={{ fontSize: 14 }} /> },
            'Pending': { bg: '#ef444415', color: '#ef4444', icon: <TimelapseIcon sx={{ fontSize: 14 }} /> },
            'Cancelled': { bg: '#6b728015', color: '#6b7280', icon: <CancelIcon sx={{ fontSize: 14 }} /> }
        };
        const config = statusConfig[status] || { bg: '#9ca3af15', color: '#9ca3af', icon: null };
        return (
            <Chip
                size="small"
                icon={config.icon}
                label={quantity !== null ? `${status} (${quantity})` : status}
                sx={{ bgcolor: config.bg, color: config.color, fontWeight: 600, fontSize: '11px' }}
            />
        );
    };

    const getDeliveryStatusChip = (order) => {
    const status = order.Tfn_ReqSt || 'Unknown';
   
    return getStatusChip(status);
};

    // Format number
    const formatNumber = (num) => {
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num.toString();
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return dayjs(date).format('DD/MM/YYYY');
    };

    // Format time
    const formatTime = (date) => {
        if (!date) return 'N/A';
        return dayjs(date).format('hh:mm A');
    };

    // Export to PDF - FIXED VERSION (no TypeScript syntax)
const exportToPDF = async () => {
    setExporting(true);
    try {
        const pdf = new jsPDF('landscape', 'mm', 'a4');
        const pageWidth = pdf.internal.pageSize.getWidth();
        
        // Header
        pdf.setFillColor(99, 91, 255);
        pdf.rect(0, 0, pageWidth, 40, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(20);
        pdf.setFont("helvetica", "bold");
        pdf.text('Delivery Route Report', pageWidth / 2, 15, { align: 'center' });
        pdf.setFontSize(10);
        pdf.text(`Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`, pageWidth / 2, 25, { align: 'center' });
        pdf.text(`Generated on: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`, pageWidth / 2, 32, { align: 'center' });
        
        // Summary Section
        let yPos = 50;
        pdf.setFillColor(240, 242, 245);
        pdf.rect(10, yPos, pageWidth - 20, 35, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.text('Summary Statistics', 15, yPos + 7);
        
        pdf.setFontSize(9);
        pdf.setFont("helvetica", "normal");
        const summaryData = [
            [`Total Orders: ${deliveryStats.totalOrders}`, `Total Tiffins: ${deliveryStats.totalQuantity}`],
            [`Delivered: ${deliveryStats.deliveredQuantity}`, `Returned: ${deliveryStats.returnedQuantity}`],
            [`Pending: ${deliveryStats.pendingQuantity}`, `Completion Rate: ${deliveryStats.completionRate.toFixed(1)}%`],
            [`Return Rate: ${deliveryStats.returnRate.toFixed(1)}%`, `On-Time Rate: ${deliveryStats.onTimeRate.toFixed(1)}%`]
        ];
        
        let summaryY = yPos + 12;
        for (let i = 0; i < summaryData.length; i++) {
            pdf.text(summaryData[i][0], 15, summaryY);
            pdf.text(summaryData[i][1], pageWidth / 2 + 10, summaryY);
            summaryY += 6;
        }
        
        yPos += 45;
        
        // Route Summary Table
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(99, 91, 255);
        pdf.text('Route-wise Summary', 15, yPos);
        
        const routeTableData = routeStats.map(route => [
            route.routeNo,
            route.totalOrders,
            route.totalQuantity,
            route.deliveredQuantity,
            route.returnedQuantity,
            route.completionRate.toFixed(1) + '%',
            route.institutes
        ]);
        
        pdf.autoTable({
            startY: yPos + 5,
            head: [['Route', 'Orders', 'Qty', 'Delivered', 'Returned', 'Completion', 'Institutes']],
            body: routeTableData,
            theme: 'striped',
            headStyles: { fillColor: [99, 91, 255], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 20 },
                1: { cellWidth: 25 },
                2: { cellWidth: 25 },
                3: { cellWidth: 30 },
                4: { cellWidth: 30 },
                5: { cellWidth: 30 },
                6: { cellWidth: 30 }
            },
            margin: { left: 15, right: 15 }
        });
        
        // Orders Table - FIXED: removed TypeScript 'as any' syntax
        let finalY = pdf.autoTable.previous.finalY + 10;
        pdf.setFontSize(12);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(99, 91, 255);
        pdf.text('Order Details', 15, finalY);
        
        const orderTableData = filteredOrders.slice(0, 50).map(order => [
            order.Tfn_ReqId,
            formatDate(order.Req_Date),
            order.InstituteName?.slice(0, 20) || 'N/A',
            order.ResidentName?.slice(0, 15) || 'N/A',
            order.RouteNo || 'N/A',
            order.Req_Qty || 0,
            order.Dlv_Qty || 0,
            order.Rtn_Qty || 0,
            order.Tfn_ReqSt || 'N/A'
        ]);
        
        pdf.autoTable({
            startY: finalY + 5,
            head: [['ID', 'Date', 'Institute', 'Resident', 'Route', 'Qty', 'Del', 'Ret', 'Status']],
            body: orderTableData,
            theme: 'striped',
            headStyles: { fillColor: [99, 91, 255], textColor: [255, 255, 255], fontSize: 8 },
            bodyStyles: { fontSize: 7 },
            margin: { left: 15, right: 15 }
        });
        
        // Footer note for limited records - FIXED: removed TypeScript 'as any' syntax
        if (filteredOrders.length > 50) {
            let noteY = pdf.autoTable.previous.finalY + 5;
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`* Showing 50 of ${filteredOrders.length} orders`, 15, noteY);
        }
        
        // Footer
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);
            pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pdf.internal.pageSize.getHeight() - 10);
            pdf.text('Delivery Route Dashboard', 15, pdf.internal.pageSize.getHeight() - 10);
        }
        
        pdf.save(`Delivery_Report_${startDate.format('YYYY-MM-DD')}_to_${endDate.format('YYYY-MM-DD')}.pdf`);
        toast.success('PDF exported successfully!');
    } catch (error) {
        console.error('PDF export error:', error);
        toast.error('Failed to export PDF');
    } finally {
        setExporting(false);
    }
};

    // View order details
    const viewOrderDetails = (order) => {
        setSelectedOrder(order);
        setDetailsOpen(true);
    };

    // Fetch data on mount
    useEffect(() => {
        if (isPropertyReady && globalProperty) {
            fetchOrders();
        }
    }, [isPropertyReady, globalProperty, pageNumber, pageSize]);

    // Apply filters
    useEffect(() => {
        if (orders.length > 0) {
            applyFilters();
        }
    }, [selectedRoute, searchText, orders]);

    // Date filter
    useEffect(() => {
        if (orders.length > 0) {
            let filtered = [...orders];
            filtered = filtered.filter(order => {
                if (!order.Req_Date) return false;
                const orderDate = dayjs(order.Req_Date);
                return orderDate.isAfter(startDate.subtract(1, 'day')) && orderDate.isBefore(endDate.add(1, 'day'));
            });
            if (selectedRoute !== 'ALL') {
                filtered = filtered.filter(order => order.RouteNo === selectedRoute);
            }
            setFilteredOrders(filtered);
            calculateStats(filtered);
            processDailyTrends(filtered);
            processInstituteStats(filtered);
            processStatusDistribution(filtered);
        }
    }, [startDate, endDate]);

    const sortedOrders = getSortedOrders();

    // Route Detail Modal Component
    const RouteDetailModal = () => (
        <Dialog open={routeDetailOpen} onClose={() => setRouteDetailOpen(false)} maxWidth="md" fullWidth>
            {selectedRouteDetail && (
                <>
                    <DialogTitle sx={{ bgcolor: COLORS.primary, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RouteIcon /> Route {selectedRouteDetail.routeNo} - Details
                        </Box>
                        <IconButton onClick={() => setRouteDetailOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        {/* Route Summary */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.primary}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.primary }}>{selectedRouteDetail.totalOrders}</Typography>
                                    <Typography variant="caption">Total Orders</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.success}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.success }}>{selectedRouteDetail.totalQuantity}</Typography>
                                    <Typography variant="caption">Total Tiffins</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.warning}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.warning }}>{selectedRouteDetail.deliveredQuantity}</Typography>
                                    <Typography variant="caption">Delivered</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.info}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.info }}>{selectedRouteDetail.completionRate.toFixed(1)}%</Typography>
                                    <Typography variant="caption">Completion Rate</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Institutes List */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.primary }}>🏛️ Institutes on this Route</Typography>
                        <Grid container spacing={1} sx={{ mb: 3 }}>
                            {selectedRouteDetail.institutesList.map((inst, idx) => (
                                <Grid item key={idx}>
                                    <Chip label={inst} sx={{ bgcolor: COLORS.primary + '10', color: COLORS.primary }} />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Locations List */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.primary }}>📍 Delivery Locations</Typography>
                        <Grid container spacing={1} sx={{ mb: 3 }}>
                            {selectedRouteDetail.locationsList.map((loc, idx) => (
                                <Grid item key={idx}>
                                    <Chip label={loc} variant="outlined" sx={{ borderColor: COLORS.info, color: COLORS.info }} />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Orders on this Route */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.primary }}>📋 Orders on this Route</Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Institute</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell align="center">Qty</TableCell>
                                        <TableCell align="center">Delivered</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedRouteDetail.orders.slice(0, 20).map((order, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell>#{order.Tfn_ReqId}</TableCell>
                                            <TableCell>{formatDate(order.Req_Date)}</TableCell>
                                            <TableCell>{order.InstituteName?.slice(0, 25)}</TableCell>
                                            <TableCell>{order.Dlv_Loc?.slice(0, 20)}</TableCell>
                                            <TableCell align="center">{order.Req_Qty}</TableCell>
                                            <TableCell align="center">{order.Dlv_Qty || 0}</TableCell>
                                            <TableCell>{getStatusChip(order.Tfn_ReqSt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {selectedRouteDetail.orders.length > 20 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Showing 20 of {selectedRouteDetail.orders.length} orders
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRouteDetailOpen(false)} variant="outlined">Close</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );

    // Institute Detail Modal Component
    const InstituteDetailModal = () => (
        <Dialog open={instituteDetailOpen} onClose={() => setInstituteDetailOpen(false)} maxWidth="md" fullWidth>
            {selectedInstituteDetail && (
                <>
                    <DialogTitle sx={{ bgcolor: COLORS.primary, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SchoolIcon /> {selectedInstituteDetail.name} - Details
                        </Box>
                        <IconButton onClick={() => setInstituteDetailOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        {/* Institute Summary */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.primary}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.primary }}>{selectedInstituteDetail.totalOrders}</Typography>
                                    <Typography variant="caption">Total Orders</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.success}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.success }}>{selectedInstituteDetail.totalQuantity}</Typography>
                                    <Typography variant="caption">Total Tiffins</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.warning}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.warning }}>{selectedInstituteDetail.deliveredQuantity}</Typography>
                                    <Typography variant="caption">Delivered</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${COLORS.info}10` }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, color: COLORS.info }}>{selectedInstituteDetail.completionRate.toFixed(1)}%</Typography>
                                    <Typography variant="caption">Completion Rate</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Routes Serving this Institute */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.primary }}>🚚 Routes Serving this Institute</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                            {selectedInstituteDetail.routesList.map((route, idx) => (
                                <Chip key={idx} label={`Route ${route}`} sx={{ bgcolor: COLORS.success + '15', color: COLORS.success, fontWeight: 600 }} />
                            ))}
                        </Box>

                        {/* Orders for this Institute */}
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: COLORS.primary }}>📋 Orders from this Institute</Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Route</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell align="center">Qty</TableCell>
                                        <TableCell align="center">Delivered</TableCell>
                                        <TableCell>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedInstituteDetail.orders.slice(0, 20).map((order, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell>#{order.Tfn_ReqId}</TableCell>
                                            <TableCell>{formatDate(order.Req_Date)}</TableCell>
                                            <TableCell>
                                                <Chip label={order.RouteNo || 'N/A'} size="small" sx={{ bgcolor: COLORS.primary + '15', color: COLORS.primary }} />
                                            </TableCell>
                                            <TableCell>{order.Dlv_Loc?.slice(0, 20)}</TableCell>
                                            <TableCell align="center">{order.Req_Qty}</TableCell>
                                            <TableCell align="center">{order.Dlv_Qty || 0}</TableCell>
                                            <TableCell>{getStatusChip(order.Tfn_ReqSt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {selectedInstituteDetail.orders.length > 20 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Showing 20 of {selectedInstituteDetail.orders.length} orders
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setInstituteDetailOpen(false)} variant="outlined">Close</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );

    // Order Details Drawer
    const OrderDetailsDrawer = () => (
        <Drawer anchor="right" open={detailsOpen} onClose={() => setDetailsOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 450 } } }}>
            {selectedOrder && (
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.primary }}>Order #{selectedOrder.Tfn_ReqId}</Typography>
                        {getStatusChip(selectedOrder.Tfn_ReqSt)}
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.primary, mb: 2 }}>Order Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Request Date</Typography><Typography variant="body2">{formatDate(selectedOrder.Req_Date)}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Meal Type</Typography><Typography variant="body2">{selectedOrder.MealTypeName || 'N/A'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Quantity</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>{selectedOrder.Req_Qty || 0}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Route No</Typography><Chip label={selectedOrder.RouteNo || 'N/A'} size="small" sx={{ bgcolor: COLORS.primary + '15', color: COLORS.primary }} /></Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.primary, mb: 2 }}>Customer Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SchoolIcon sx={{ fontSize: 18, color: COLORS.info }} /><Typography variant="body2">{selectedOrder.InstituteName || 'N/A'}</Typography></Box></Grid>
                        <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon sx={{ fontSize: 18, color: COLORS.info }} /><Typography variant="body2">{selectedOrder.ResidentName || 'N/A'}</Typography></Box></Grid>
                        <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon sx={{ fontSize: 18, color: COLORS.info }} /><Typography variant="body2">{selectedOrder.MobileNo || 'N/A'}</Typography></Box></Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.primary, mb: 2 }}>Delivery Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationOnIcon sx={{ fontSize: 18, color: COLORS.success }} /><Typography variant="body2">Location: {selectedOrder.Dlv_Loc || 'N/A'}</Typography></Box></Grid>
                        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Delivery OTP</Typography><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: COLORS.primary }}>{selectedOrder.Dlv_OTP || 'N/A'}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Delivered Quantity</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.success }}>{selectedOrder.Dlv_Qty || 0}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Delivery Remark</Typography><Typography variant="body2">{selectedOrder.Dlv_Remark || 'No remarks'}</Typography></Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    {selectedOrder.Rtn_Qty > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.warning, mb: 2 }}>Return Information</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}><Typography variant="caption" color="text.secondary">Return Quantity</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.warning }}>{selectedOrder.Rtn_Qty}</Typography></Grid>
                                <Grid item xs={12}><Typography variant="caption" color="text.secondary">Return Remark</Typography><Typography variant="body2">{selectedOrder.Rtn_Remark || 'No remarks'}</Typography></Grid>
                            </Grid>
                        </>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                        {/* <Button fullWidth variant="outlined" startIcon={<PhoneIcon />} onClick={() => window.location.href = `tel:${selectedOrder.MobileNo}`}>Call Customer</Button> */}
                    </Box>
                </Box>
            )}
        </Drawer>
    );

    // Stats Card Component
    const StatsCard = ({ title, value, icon, color, subtitle }) => (
        <Zoom in={true} timeout={300}>
            <Card sx={{ borderRadius: 3, overflow: 'hidden', transition: 'all 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 30px ${color}30` }, borderLeft: `4px solid ${color}` }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', letterSpacing: '0.5px', fontSize: '11px' }}>{title}</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '24px', sm: '28px' }, color: color, mt: 0.5 }}>{formatNumber(value)}</Typography>
                            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>{subtitle}</Typography>}
                        </Box>
                        <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 45, height: 45 }}>{icon}</Avatar>
                    </Box>
                </CardContent>
            </Card>
        </Zoom>
    );

    // Progress Circle
    const ProgressCircle = ({ value, label, color, size = 90 }) => {
        const [progress, setProgress] = useState(0);
        useEffect(() => { const timer = setTimeout(() => setProgress(value), 200); return () => clearTimeout(timer); }, [value]);
        const radius = (size - 10) / 2;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (progress / 100) * circumference;
        return (
            <Zoom in={true} timeout={500}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        <svg width={size} height={size}>
                            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="6" />
                            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="6" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 1s ease' }} />
                        </svg>
                        <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: color }}>{progress.toFixed(1)}%</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>{label}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Zoom>
        );
    };

    // Route Card
    const RouteCard = ({ route }) => (
        <Grow in={true} timeout={300}>
            <Card sx={{ borderRadius: 3, cursor: 'pointer', transition: 'all 0.3s ease', border: selectedRoute === route.routeNo ? `2px solid ${COLORS.primary}` : '1px solid #e2e8f0', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' } }} onClick={() => handleRouteClick(route)}>
                <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><RouteIcon sx={{ color: COLORS.primary, fontSize: 20 }} /><Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Route {route.routeNo}</Typography></Box>
                        <Chip label={`${route.totalOrders} orders`} size="small" sx={{ bgcolor: COLORS.primary + '15', color: COLORS.primary }} />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1}>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Institutes</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{route.institutes}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Locations</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{route.locations}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Total Qty</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.primary }}>{route.totalQuantity}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Delivered</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.success }}>{route.deliveredQuantity}</Typography></Grid>
                    </Grid>
                    <Box sx={{ mt: 1 }}><LinearProgress variant="determinate" value={route.completionRate} sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: COLORS.success } }} /><Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Completion: {route.completionRate.toFixed(1)}%</Typography></Box>
                    <Button size="small" sx={{ mt: 1, color: COLORS.primary }} onClick={(e) => { e.stopPropagation(); handleRouteClick(route); }}>View Details →</Button>
                </CardContent>
            </Card>
        </Grow>
    );

    // Main render
    if (!isPropertyReady) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress sx={{ color: COLORS.primary }} />
                <Typography sx={{ ml: 2, color: COLORS.primary }}>Initializing...</Typography>
            </Box>
        );
    }

    if (!globalProperty) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" color="error">No Property Selected</Typography>
                <Typography variant="body1" color="text.secondary">Please select a property to view delivery dashboard.</Typography>
                <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ bgcolor: COLORS.primary }}>Go to Dashboard</Button>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: { xs: 1, sm: 2, md: 2.5 } }}>
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <Fade in={true} timeout={600}>
                    <Paper sx={{ p: { xs: 2, sm: 2.5 }, mb: { xs: 2, sm: 3 }, borderRadius: 4, background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.indigo} 100%)`, color: 'white' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IconButton onClick={() => navigate('/masters/tiffin')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}><ArrowBackIcon /></IconButton>
                                <Box><Typography variant="h5" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}><LocalShippingIcon /> Delivery Route Dashboard</Typography><Typography variant="body2" sx={{ opacity: 0.9 }}>Manage and track deliveries by route</Typography></Box>
                            </Box>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                            <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                                <DatePicker label="From Date" value={startDate} onChange={(newValue) => setStartDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: '140px', '& .MuiInputBase-root': { color: 'white', fontSize: '13px' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } } } }} />
                            </Paper>
                            <Paper sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                                <DatePicker label="To Date" value={endDate} onChange={(newValue) => setEndDate(newValue)} slotProps={{ textField: { size: 'small', sx: { width: '140px', '& .MuiInputBase-root': { color: 'white', fontSize: '13px' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } } } }} />
                            </Paper>
                            <TextField size="small" placeholder="Search by institute, resident, mobile..." value={searchText} onChange={(e) => setSearchText(e.target.value)} sx={{ minWidth: 250, '& .MuiInputBase-root': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 } }} InputProps={{ startAdornment: <FilterListIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 18 }} /> }} />
                            <Chip icon={selectedRoute === 'ALL' ? <RouteIcon /> : <CheckCircleIcon />} label={selectedRoute === 'ALL' ? `All Routes (${routes.length})` : `Route ${selectedRoute}`} onClick={() => setSelectedRoute('ALL')} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', cursor: 'pointer' }} />
                        </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Tooltip title="Export PDF"><IconButton onClick={exportToPDF} disabled={exporting} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>{exporting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <PictureAsPdfIcon />}</IconButton></Tooltip>
                                <Tooltip title="Refresh Data"><IconButton onClick={fetchOrders} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}><RefreshIcon /></IconButton></Tooltip>
                            </Box>
                        </Box>

                        {/* Filters */}
                       

                        {(selectedRoute !== 'ALL' || searchText) && (
                            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {selectedRoute !== 'ALL' && <Chip label={`Route: ${selectedRoute}`} onDelete={() => setSelectedRoute('ALL')} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />}
                                {searchText && <Chip label={`Search: ${searchText}`} onDelete={() => setSearchText('')} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />}
                            </Box>
                        )}
                    </Paper>
                </Fade>

                {/* Error Alert */}
                <Collapse in={!!error}><Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert></Collapse>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column', gap: 2 }}><CircularProgress sx={{ color: COLORS.primary }} /><Typography sx={{ color: COLORS.primary }}>Loading delivery data...</Typography></Box>
                ) : (
                    <>
                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
                            <Tab icon={<ListAltIcon />} iconPosition="start" label="Overview" />
                            <Tab icon={<RouteIcon />} iconPosition="start" label="Routes" />
                            <Tab icon={<SchoolIcon />} iconPosition="start" label="Institutes" />
                            <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Analytics" />
                        </Tabs>

                        {/* Overview Tab */}
                        {activeTab === 0 && (
                            <>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
                                    <Grid item xs={6} sm={3}><StatsCard title="Total Orders" value={deliveryStats.totalOrders} icon={<RestaurantIcon />} color={COLORS.primary} /></Grid>
                                    <Grid item xs={6} sm={3}><StatsCard title="Total Tiffins" value={deliveryStats.totalQuantity} icon={<InventoryIcon />} color={COLORS.info} /></Grid>
                                    <Grid item xs={6} sm={3}><StatsCard title="Delivered" value={deliveryStats.deliveredQuantity} icon={<DeliveryDiningIcon />} color={COLORS.success} /></Grid>
                                    <Grid item xs={6} sm={3}><StatsCard title="Returned" value={deliveryStats.returnedQuantity} icon={<WarningAmberIcon />} color={COLORS.warning} /></Grid>
                                </Grid>


                                <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={7}>
                                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>📊 Daily Delivery Trends</Typography>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <LineChart data={dailyTrends}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#64748b" />
                                                    <YAxis tick={{ fontSize: 10 }} stroke="#64748b" />
                                                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                                    <Legend />
                                                    <Line type="monotone" dataKey="delivered" stroke={COLORS.success} strokeWidth={2} dot={{ fill: COLORS.success }} name="Delivered" />
                                                    <Line type="monotone" dataKey="returned" stroke={COLORS.warning} strokeWidth={2} dot={{ fill: COLORS.warning }} name="Returned" />
                                                    <Line type="monotone" dataKey="pending" stroke={COLORS.error} strokeWidth={2} dot={{ fill: COLORS.error }} name="Pending" />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={5}>
                                        <Paper sx={{ p: 2, borderRadius: 3, height: '100%' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>🥧 Order Status Distribution</Typography>
                                            {statusDistribution.length > 0 ? (
                                                <>
                                                    <ResponsiveContainer width="100%" height={240}>
                                                        <PieChart>
                                                            <Pie data={statusDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                                                                {statusDistribution.map((entry, index) => (<Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />))}
                                                            </Pie>
                                                            <RechartsTooltip />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                                                        {statusDistribution.map((item, idx) => (<Chip key={idx} label={`${item.name}: ${item.value}`} size="small" sx={{ bgcolor: `${CHART_COLORS[idx % CHART_COLORS.length]}15`, color: CHART_COLORS[idx % CHART_COLORS.length] }} />))}
                                                    </Box>
                                                </>
                                            ) : (<Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No data available</Typography>)}
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Fade in={true} timeout={800}>
                                    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="h6" sx={{ fontWeight: 700 }}>📋 Delivery Orders ({filteredOrders.length} records)</Typography>{selectedRoute !== 'ALL' && <Chip label={`Route ${selectedRoute}`} size="small" sx={{ bgcolor: COLORS.primary + '15', color: COLORS.primary }} />}</Box>
                                            <Box sx={{ display: 'flex', gap: 1 }}><Chip label={`✅ Delivered: ${deliveryStats.deliveredQuantity}`} size="small" sx={{ bgcolor: '#22c55e15', color: '#22c55e' }} /><Chip label={`↩️ Returned: ${deliveryStats.returnedQuantity}`} size="small" sx={{ bgcolor: '#f59e0b15', color: '#f59e0b' }} /><Chip label={`⏳ Pending: ${deliveryStats.pendingQuantity}`} size="small" sx={{ bgcolor: '#ef444415', color: '#ef4444' }} /></Box>
                                        </Box>
                                        <TableContainer sx={{ overflowX: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>ID</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}><TableSortLabel active={sortBy === 'date'} direction={sortOrder} onClick={() => { setSortBy('date'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}>Date</TableSortLabel></TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Institute</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Resident</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Route</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Location</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Qty</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Delivered</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }} align="center">Returned</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Status</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {sortedOrders.slice(0, 100).map((order, index) => (
                                                        <TableRow key={order.Tfn_ReqId || index} hover sx={{ '&:hover': { bgcolor: '#f8fafc', cursor: 'pointer' } }} onClick={() => viewOrderDetails(order)}>
                                                            <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>#{order.Tfn_ReqId}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px' }}>{formatDate(order.Req_Date)}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px' }}>{order.InstituteName?.slice(0, 25) || 'N/A'}</TableCell>
                                                            <TableCell sx={{ fontSize: '12px' }}>{order.ResidentName?.slice(0, 20) || 'N/A'}</TableCell>
                                                            <TableCell><Chip label={order.RouteNo || 'N/A'} size="small" sx={{ bgcolor: COLORS.primary + '15', color: COLORS.primary, fontWeight: 600, fontSize: '10px', height: 22 }} /></TableCell>
                                                            <TableCell sx={{ fontSize: '12px' }}>{order.Dlv_Loc?.slice(0, 20) || 'N/A'}</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '12px' }}>{order.Req_Qty || 0}</TableCell>
                                                            <TableCell align="center" sx={{ color: COLORS.success, fontWeight: 500, fontSize: '12px' }}>{order.Dlv_Qty || 0}</TableCell>
                                                            <TableCell align="center" sx={{ color: COLORS.warning, fontWeight: 500, fontSize: '12px' }}>{order.Rtn_Qty || 0}</TableCell>
                                                            <TableCell>{getDeliveryStatusChip(order)}</TableCell>
                                                            <TableCell><Tooltip title="View Details"><IconButton size="small" onClick={(e) => { e.stopPropagation(); viewOrderDetails(order); }}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip></TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {filteredOrders.length > 100 && (<Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid #e2e8f0' }}><Typography variant="caption" color="text.secondary">Showing 100 of {filteredOrders.length} orders</Typography></Box>)}
                                    </Paper>
                                </Fade>
                            </>
                        )}

                        {/* Routes Tab */}
                        {activeTab === 1 && (
                            <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: 3 }}>
                                {routeStats.map((route, idx) => (<Grid item xs={12} sm={6} md={4} key={idx}><RouteCard route={route} /></Grid>))}
                                {routeStats.length === 0 && (<Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}><RouteIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} /><Typography color="text.secondary">No routes found for the selected criteria</Typography></Paper>)}
                            </Grid>
                        )}

                        {/* Institutes Tab */}
                        {activeTab === 2 && (
                            <Grid container spacing={{ xs: 2, sm: 2 }}>
                                {instituteStats.map((institute, idx) => (
                                    <Grid item xs={12} sm={6} md={4} key={idx}>
                                        <Zoom in={true} timeout={300 + idx * 50}>
                                            <Paper sx={{ p: 2, borderRadius: 3, cursor: 'pointer', transition: 'all 0.3s', '&:hover': { transform: 'translateX(5px)', boxShadow: '0 5px 20px rgba(0,0,0,0.1)' } }} onClick={() => handleInstituteClick(institute)}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SchoolIcon sx={{ color: COLORS.primary, fontSize: 20 }} /><Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{institute.name.slice(0, 30)}</Typography></Box>
                                                    <Chip label={`${institute.routes} routes`} size="small" sx={{ bgcolor: COLORS.info + '15', color: COLORS.info }} />
                                                </Box>
                                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                                    <Grid item xs={4}><Typography variant="caption" color="text.secondary">Orders</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{institute.totalOrders}</Typography></Grid>
                                                    <Grid item xs={4}><Typography variant="caption" color="text.secondary">Qty</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>{institute.totalQuantity}</Typography></Grid>
                                                    <Grid item xs={4}><Typography variant="caption" color="text.secondary">Delivered</Typography><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.success }}>{institute.deliveredQuantity}</Typography></Grid>
                                                </Grid>
                                                <LinearProgress variant="determinate" value={institute.completionRate} sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: COLORS.success } }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Completion: {institute.completionRate.toFixed(1)}%</Typography>
                                                <Button size="small" sx={{ mt: 1, color: COLORS.primary }} onClick={(e) => { e.stopPropagation(); handleInstituteClick(institute); }}>View Details →</Button>
                                            </Paper>
                                        </Zoom>
                                    </Grid>
                                ))}
                            </Grid>
                        )}

                        {/* Analytics Tab */}
                        {activeTab === 3 && (
                            <>
                                <Grid container spacing={{ xs: 2, sm: 2 }} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>📊 Route Performance</Typography>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart data={routeStats} layout="vertical" margin={{ left: 40 }}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" tick={{ fontSize: 10 }} />
                                                    <YAxis type="category" dataKey="routeNo" tick={{ fontSize: 11 }} />
                                                    <RechartsTooltip />
                                                    <Legend />
                                                    <Bar dataKey="deliveredQuantity" name="Delivered" fill={COLORS.success} radius={[0, 4, 4, 0]} />
                                                    <Bar dataKey="returnedQuantity" name="Returned" fill={COLORS.warning} radius={[0, 4, 4, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Paper sx={{ p: 2, borderRadius: 3 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>📈 Top Institutes by Volume</Typography>
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart data={instituteStats} layout="vertical" margin={{ left: 100 }}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis type="number" tick={{ fontSize: 10 }} />
                                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                                                    <RechartsTooltip />
                                                    <Bar dataKey="totalQuantity" name="Total Quantity" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Paper>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    <Grid item xs={12} sm={4}><Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: `${COLORS.primary}05` }}><Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.primary }}>{routeStats.length}</Typography><Typography variant="body2" color="text.secondary">Active Routes</Typography></Paper></Grid>
                                    <Grid item xs={12} sm={4}><Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: `${COLORS.success}05` }}><Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.success }}>{instituteStats.length}</Typography><Typography variant="body2" color="text.secondary">Institutes Served</Typography></Paper></Grid>
                                    <Grid item xs={12} sm={4}><Paper sx={{ p: 2, borderRadius: 3, textAlign: 'center', bgcolor: `${COLORS.info}05` }}><Typography variant="h3" sx={{ fontWeight: 800, color: COLORS.info }}>{deliveryStats.onTimeRate.toFixed(1)}%</Typography><Typography variant="body2" color="text.secondary">On-Time Delivery Rate</Typography></Paper></Grid>
                                </Grid>
                            </>
                        )}

                        {/* Summary Footer */}
                        <Fade in={true} timeout={1000}>
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">🕐 Last updated: {dayjs().format('DD/MM/YYYY HH:mm:ss')}</Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocalShippingIcon sx={{ fontSize: 12 }} /> Routes: {routeStats.length}</Typography>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.success }}><DeliveryDiningIcon sx={{ fontSize: 12 }} /> Delivered: {deliveryStats.deliveredQuantity}</Typography>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: COLORS.warning }}><WarningAmberIcon sx={{ fontSize: 12 }} /> Returned: {deliveryStats.returnedQuantity}</Typography>
                                </Box>
                            </Box>
                        </Fade>
                    </>
                )}

                {/* Modals */}
                <RouteDetailModal />
                <InstituteDetailModal />
                <OrderDetailsDrawer />
            </Box>
        </LocalizationProvider>
    );
};

export default DeliveryRouteDashboard;